import { useState, useCallback, useEffect } from 'react';
import { useMount } from 'zoom/hooks';
import { ZoomClient } from 'index-types';
import { MAX_NUMBER_PER_PAGE } from 'utils/video-layout-helper';

/**
 * This function watches and manages the pagination related states, and returns
 * those, like page (it doesn't modify, but controls the state), page setter, page size (calculated),
 * total size of participants, and all that related to participants moving in and out
 * @param zmClient, current zoom client
 * @param dimension , dimension of the video layout
 * @returns object with relevant information, like page number, page number setter, page size, total size of
 * participants
 */
export function usePagination(zmClient: ZoomClient) {
  // current page number
  const [page, setPage] = useState(0);

  // total amount of users
  const [totalSize, setTotalSize] = useState(0);

  /**
   * This function gets amount of all users inside the session, and,
   * if there are more than two users, sets the size to two
   */
  const onParticipantsChange = () => {
    const size = zmClient.getAllUser().length;
    setTotalSize(size);
  };
  // this callback is memorized, and recalculated every zmClient change
  const onParticipantsChangeMemorized = useCallback(onParticipantsChange, [zmClient]);

  // this useffect sets the listeners on all the relevant events, which
  // signify that user numbers has changed. the cleaner is
  // turning the listeners off. The recalculation is triggered by changes
  // rather in function and zmClient, that is the same thing, due to callback
  // recalculations
  useEffect(() => {
    zmClient.on('user-added', onParticipantsChangeMemorized);
    zmClient.on('user-removed', onParticipantsChangeMemorized);
    zmClient.on('user-updated', onParticipantsChangeMemorized);
    return () => {
      zmClient.off('user-added', onParticipantsChangeMemorized);
      zmClient.off('user-removed', onParticipantsChangeMemorized);
      zmClient.off('user-updated', onParticipantsChangeMemorized);
    };
  }, [zmClient, onParticipantsChangeMemorized]);
  /**
   * This hook watches the size amount , sets it and recalculates if the size changed
   */
  useMount(() => {
    setTotalSize(zmClient.getAllUser().length);
  });
  return {
    page,
    totalPage: Math.ceil(totalSize / MAX_NUMBER_PER_PAGE),
    pageSize: MAX_NUMBER_PER_PAGE,
    totalSize,
    setPage,
  };
}
